import React from 'react';
import type { CanvasObject } from '../../../types';

type Props = {
	getSelectedObjects: () => CanvasObject[];
	updateObject: (id: string, updater: (o: CanvasObject) => CanvasObject) => void;
	requestRender: () => void;
	saveToHistory: (state: { objects: Map<string, CanvasObject>; zOrder: string[]; selectedId: string | null }) => void;
	objects: Map<string, CanvasObject>;
	zOrder: string[];
	selectedId: string | null;
};

export const BlendModeControl: React.FC<Props> = ({
	getSelectedObjects,
	updateObject,
	requestRender,
	saveToHistory,
	objects,
	zOrder,
	selectedId
}) => {
	// 표시용 값 계산 (다중 선택 시 첫 번째 객체 기준)
	const displayedBlendMode = React.useMemo(() => {
		const selected = getSelectedObjects();
		if (selected.length === 0) return 'normal';
		return selected[0].blendMode || 'normal';
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getSelectedObjects, selectedId, objects]);

	const handleChange = React.useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value as CanvasObject['blendMode'];
		const selected = getSelectedObjects();
		if (selected.length === 0) return;
		
		selected.forEach(obj => {
			updateObject(obj.id, (o) => ({ ...o, blendMode: value }));
		});
		requestRender();
		saveToHistory({ objects: new Map(objects), zOrder: [...zOrder], selectedId });
	}, [getSelectedObjects, updateObject, requestRender, saveToHistory, objects, zOrder, selectedId]);

	const selected = getSelectedObjects();
	const isDisabled = selected.length === 0;

	return (
		<div className="info-field" style={{ padding: '6px 0 6px' }}>
			<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
				<label className="info-panel__label" style={{ margin: 0, whiteSpace: 'nowrap' }}>BLEND</label>
				<select
					className="info-panel__input"
					style={{ flex: '1 1 auto' }}
					value={displayedBlendMode}
					onChange={handleChange}
					disabled={isDisabled}
				>
					<optgroup label="기본">
						<option value="normal">Normal</option>
					</optgroup>
					<optgroup label="Darken">
						<option value="darken">Darken</option>
						<option value="multiply">Multiply</option>
						<option value="color-burn">Color Burn</option>
					</optgroup>
					<optgroup label="Lighten">
						<option value="lighten">Lighten</option>
						<option value="screen">Screen</option>
						<option value="color-dodge">Color Dodge</option>
						<option value="plus-lighter">Plus Lighter</option>
					</optgroup>
					<optgroup label="Contrast">
						<option value="overlay">Overlay</option>
						<option value="soft-light">Soft Light</option>
						<option value="hard-light">Hard Light</option>
					</optgroup>
					<optgroup label="Comparative">
						<option value="difference">Difference</option>
						<option value="exclusion">Exclusion</option>
					</optgroup>
					<optgroup label="Component">
						<option value="hue">Hue</option>
						<option value="saturation">Saturation</option>
						<option value="color">Color</option>
						<option value="luminosity">Luminosity</option>
					</optgroup>
				</select>
			</div>
		</div>
	);
};

